import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2, Mail, Send, AlertTriangle, ShieldCheck, Clock, Eye, EyeOff, User, Building2, Calendar, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, fetchWithAuth } from './constants';

export const PitchEmailModal = ({
  roleId,
  roleTitle,
  company,
  candidateKeys,
  candidateNames,
  pitchText,
  pitchType,
  submissionId,
  onClose,
  onSent,
}) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [dailyStatus, setDailyStatus] = useState(null);
  const [config, setConfig] = useState(null);

  // Form fields
  const [toName, setToName] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState(`Top Candidate${candidateKeys?.length > 1 ? 's' : ''} for ${roleTitle} — ${company}`);
  const [bodyHtml, setBodyHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Confirmation
  const [reviewed, setReviewed] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  // Scheduling
  const [sendMode, setSendMode] = useState('now'); // 'now' | 'schedule' | 'smart'
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');

  // Business hours check
  const isOutsideBusinessHours = useMemo(() => {
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay();
    return hour < 8 || hour >= 17 || day === 0 || day === 6;
  }, []);

  // Smart schedule: next business hour
  const smartScheduleTime = useMemo(() => {
    const now = new Date();
    let target = new Date(now);
    const hour = now.getHours();
    const day = now.getDay();

    if (hour >= 17 || day === 0 || day === 6) {
      // After hours or weekend: push to next business day 9 AM
      if (day === 6) target.setDate(target.getDate() + 2);
      else if (day === 0) target.setDate(target.getDate() + 1);
      else target.setDate(target.getDate() + 1);
      target.setHours(9, 0, 0, 0);
    } else if (hour < 8) {
      target.setHours(9, 0, 0, 0);
    }
    return target;
  }, []);

  useEffect(() => {
    // Convert pitch text to HTML (preserve newlines)
    if (pitchText) {
      const html = pitchText
        .split('\n')
        .map(line => line.trim() === '' ? '<br/>' : `<p style="margin:0 0 8px 0;font-family:Arial,sans-serif;font-size:14px;line-height:1.6;color:#333;">${line}</p>`)
        .join('\n');
      setBodyHtml(html);
    }
  }, [pitchText]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [status, cfg] = await Promise.all([
          fetchWithAuth(`${API_URL}/api/intelligence/acquisition/pitch-email/daily-status`),
          fetchWithAuth(`${API_URL}/api/intelligence/acquisition/pitch-email/config`),
        ]);
        setDailyStatus(status);
        setConfig(cfg);
      } catch {
        toast.error('Failed to load email configuration');
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleSend = async () => {
    if (!reviewed || !confirmSend) {
      toast.error('Please confirm both checkboxes before sending');
      return;
    }
    if (!toEmail || !toEmail.includes('@')) {
      toast.error('Please enter a valid recipient email');
      return;
    }
    if (!subject.trim()) {
      toast.error('Subject line cannot be empty');
      return;
    }

    setSending(true);
    const token = localStorage.getItem('backoffice_token');
    const basePayload = {
      to_email: toEmail,
      to_name: toName,
      company: company,
      subject: subject,
      body_html: bodyHtml,
      role_id: roleId || '',
      submission_id: submissionId || '',
      candidate_keys: candidateKeys || [],
      pitch_type: pitchType || 'short',
    };

    try {
      let endpoint = `${API_URL}/api/intelligence/acquisition/pitch-email/send`;
      let payload = basePayload;

      if (sendMode === 'schedule') {
        if (!scheduleDate || !scheduleTime) {
          toast.error('Please select a date and time');
          setSending(false);
          return;
        }
        endpoint = `${API_URL}/api/intelligence/acquisition/pitch-email/schedule`;
        payload = { ...basePayload, scheduled_for: `${scheduleDate}T${scheduleTime}:00`, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
      } else if (sendMode === 'smart') {
        endpoint = `${API_URL}/api/intelligence/acquisition/pitch-email/schedule`;
        payload = { ...basePayload, scheduled_for: smartScheduleTime.toISOString(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.error) {
        toast.error(data.detail);
      } else {
        const msg = sendMode === 'now' ? (data.message || 'Pitch email sent') : `Email scheduled for ${sendMode === 'smart' ? smartScheduleTime.toLocaleString() : `${scheduleDate} ${scheduleTime}`}`;
        toast.success(msg);
        if (onSent) onSent(data);
        onClose();
      }
    } catch {
      toast.error('Failed to process email');
    }
    setSending(false);
  };

  const limitReached = dailyStatus?.limit_reached;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      data-testid="pitch-email-modal"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#0A1F44]">Send Pitch via Email</h2>
              <p className="text-[10px] text-amber-600 font-medium">Sending disabled — domain verification required before emails can be sent</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {dailyStatus && (
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${
                limitReached ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'
              }`} data-testid="daily-send-counter">
                <Clock className="h-3 w-3" />
                {dailyStatus.sent_today}/{dailyStatus.daily_limit} today
              </div>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="close-email-modal">
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading email configuration...
          </div>
        ) : limitReached ? (
          <div className="p-8 text-center">
            <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
            <h3 className="text-sm font-bold text-[#0A1F44] mb-1">Daily Send Limit Reached</h3>
            <p className="text-xs text-gray-500 mb-4">
              You've sent {dailyStatus.sent_today} of {dailyStatus.daily_limit} emails today.
              This limit protects your domain reputation.
            </p>
            <button onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              data-testid="limit-reached-close">
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Recipient Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1 block">Recipient Name</label>
                  <div className="relative">
                    <User className="h-3.5 w-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input type="text" value={toName} onChange={e => setToName(e.target.value)}
                      placeholder="e.g. John Smith"
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      data-testid="recipient-name-input" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1 block">Recipient Email *</label>
                  <div className="relative">
                    <Mail className="h-3.5 w-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)}
                      placeholder="recipient@company.com"
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      data-testid="recipient-email-input" />
                  </div>
                </div>
              </div>

              {/* Company & Role Context */}
              <div className="flex items-center gap-3 p-2.5 bg-[#0A1F44]/5 rounded-lg">
                <Building2 className="h-4 w-4 text-[#0A1F44]/50" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#0A1F44] truncate">{roleTitle} — {company}</p>
                  <p className="text-[10px] text-gray-500">{candidateNames?.join(', ')}</p>
                </div>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[9px] rounded capitalize flex-shrink-0">{pitchType} pitch</span>
              </div>

              {/* Sending Inbox */}
              <div className="flex items-center gap-3 p-2.5 bg-blue-50 border border-blue-100 rounded-lg">
                <Send className="h-3.5 w-3.5 text-blue-500" />
                <div className="flex-1">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Sending From</p>
                  <p className="text-xs font-medium text-[#0A1F44]">{config?.from_email}</p>
                </div>
              </div>

              {/* Subject Line */}
              <div>
                <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1 block">Subject Line</label>
                <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
                  className="w-full px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  data-testid="subject-input" />
              </div>

              {/* Email Body */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Email Body</label>
                  <button onClick={() => setShowPreview(p => !p)}
                    className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-700"
                    data-testid="toggle-email-preview">
                    {showPreview ? <><EyeOff className="h-3 w-3" /> Edit</> : <><Eye className="h-3 w-3" /> Preview</>}
                  </button>
                </div>
                {showPreview ? (
                  <div className="w-full min-h-[220px] p-4 text-sm border border-gray-200 rounded-lg bg-gray-50 overflow-y-auto"
                    data-testid="email-preview"
                    dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                ) : (
                  <textarea value={bodyHtml} onChange={e => setBodyHtml(e.target.value)}
                    className="w-full min-h-[220px] p-4 text-sm text-[#0A1F44] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 leading-relaxed resize-y font-mono"
                    data-testid="email-body-editor" />
                )}
                <p className="text-[9px] text-gray-400 mt-1">Edit the email content above. Toggle Preview to see how it will look.</p>
              </div>

              {/* Send Timing */}
              <div className="space-y-2" data-testid="send-timing">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Send Timing</p>
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => setSendMode('now')}
                    className={`p-2.5 rounded-lg border text-xs font-medium transition-all ${sendMode === 'now' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    data-testid="send-mode-now">
                    <Send className="h-3.5 w-3.5 mx-auto mb-1" /> Send Now
                  </button>
                  <button onClick={() => setSendMode('smart')}
                    className={`p-2.5 rounded-lg border text-xs font-medium transition-all ${sendMode === 'smart' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    data-testid="send-mode-smart">
                    <Clock className="h-3.5 w-3.5 mx-auto mb-1" /> Smart Schedule
                  </button>
                  <button onClick={() => setSendMode('schedule')}
                    className={`p-2.5 rounded-lg border text-xs font-medium transition-all ${sendMode === 'schedule' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    data-testid="send-mode-schedule">
                    <Calendar className="h-3.5 w-3.5 mx-auto mb-1" /> Pick Time
                  </button>
                </div>

                {sendMode === 'now' && isOutsideBusinessHours && (
                  <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-100 rounded-lg" data-testid="outside-hours-warning">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[11px] text-amber-700 font-medium">Outside business hours</p>
                      <p className="text-[10px] text-amber-600">Emails sent after hours have lower reply rates. Consider using Smart Schedule instead.</p>
                    </div>
                  </div>
                )}

                {sendMode === 'smart' && (
                  <div className="flex items-center gap-2 p-2.5 bg-blue-50 border border-blue-100 rounded-lg">
                    <Clock className="h-3.5 w-3.5 text-blue-500" />
                    <p className="text-[11px] text-blue-700">
                      Will send at <strong>{smartScheduleTime.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong> (next business hour window)
                    </p>
                  </div>
                )}

                {sendMode === 'schedule' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] text-gray-500 uppercase mb-0.5 block">Date</label>
                      <input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        data-testid="schedule-date" />
                    </div>
                    <div>
                      <label className="text-[9px] text-gray-500 uppercase mb-0.5 block">Time (business hours)</label>
                      <select value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        data-testid="schedule-time">
                        {['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00'].map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirmation Checkboxes */}
              <div className="space-y-2 pt-2" data-testid="email-confirmations">
                <label className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                  <input type="checkbox" checked={reviewed} onChange={e => setReviewed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="confirm-reviewed-checkbox" />
                  <span className="text-xs text-[#0A1F44] leading-relaxed">
                    <strong>I have reviewed this message.</strong> I have verified the recipient, subject line, and email content are correct.
                  </span>
                </label>
                <label className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                  <input type="checkbox" checked={confirmSend} onChange={e => setConfirmSend(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="confirm-send-checkbox" />
                  <span className="text-xs text-[#0A1F44] leading-relaxed">
                    <strong>I want to send this email.</strong> This will deliver the pitch directly to the recipient's inbox.
                  </span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-xl">
              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                <span>{dailyStatus?.remaining} sends remaining today</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onClose}
                  className="px-4 py-2 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
                  data-testid="cancel-email-btn">
                  Cancel
                </button>
                <button
                  disabled
                  className="px-5 py-2 text-xs font-bold rounded-lg flex items-center gap-1.5 bg-gray-200 text-gray-400 cursor-not-allowed"
                  data-testid="send-email-btn"
                  title="Domain verification required — Resend sending is disabled">
                  <Lock className="h-3.5 w-3.5" /> Sending Disabled
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
